import { JobQueue } from "@app/jobs/job-queues/jobQueue";
import { SortDirections } from "@app/shared/waffles";
import { JobQueueMapListItem } from "@app/jobs/job-queue-mapping/jobQueueMapListItem";
import { JobQueueMapSummary } from "@app/jobs/job-queue-mapping/jobQueueMapSummary";
import { JobType } from "@app/jobs/jobType";
import ServiceBase from "@app/shared/serviceBase";
import { querystringify } from "@app/shared/utility/querystringify";
import { JobQueueMapModel } from "@app/jobs/job-queue-mapping/JobQueueMapModel";
import { EnumerationItemModel } from "@app/shared/interfaces/enumerationItemModel";

export default class JobQueueService extends ServiceBase {
  public async getJobQueues(): Promise<JobQueue[]> {
    return await this.makeGet<JobQueue[]>(`jobqueues`);
  }

  public async getAvailableJobQueues(allowDirtyReads?: boolean): Promise<JobQueue[]> {
    const query = {
      allowDirtyReads: allowDirtyReads ?? false
    }

    return await this.makeGet<JobQueue[]>(`jobqueues/available?${querystringify(query)}`);
  }

  public async getJobQueueMapSummary(skip: number, take: number, jobTypes: string[],
    sortedColumn?: string, sortDirection?: SortDirections, jobQueueFilter?: string, jobQueueIdFilter?: number, clientFilter?: string, clientIdFilter?: number, projectFilter?: string, projectIdFilter?: number, countySSAFilter?: string): Promise<JobQueueMapSummary> {
    const requestModel = {
      clientFilter,
      clientIdFilter,
      countySSAFilter,
      jobQueueFilter,
      jobQueueIdFilter,
      offset: skip,
      pageSize: take,
      projectFilter,
      projectIdFilter,
      selectedJobTypes: jobTypes,
      sortDirection,
      sortedColumn,
    };

    return await this.makePost<JobQueueMapSummary>(`jobqueues/map/summary`, requestModel);
  }

  public async jobQueueMapExists( request: Partial<JobQueueMapModel> ): Promise<boolean> {
    return await this.makePost<boolean>( `jobqueues/map/exists`, request
    );
  }

  public async deleteJobQueueMaps(jobQueueMapIds: string[]): Promise<void> {
    return await this.makeDelete<void>(`jobqueues/map`, jobQueueMapIds.map((s) => +s));
  }

  public async getJobQueue(jobType: JobType, clientId: number, projectId: number, countySSA: string): Promise<JobQueue> {
    const jobTypeValue = Object.entries(JobType).find((t) => t[1] === jobType)[0];
    return await this.makeGet<JobQueue>(`jobqueues/map?jobTypeId=${jobTypeValue}&clientId=${clientId}&projectId=${projectId}&countySSA=${countySSA}`);
  }

  public async updateJobQueue(jobQueue: JobQueue) : Promise<JobQueue> {
    return await this.makePost<JobQueue>(`jobqueues`, jobQueue);
  }

  public async deleteJobQueue(id: number) : Promise<void> {
    return await this.makeDelete<void>(`jobqueues/${id}`);
  }

  public async upsertJobQueueMap(
    request: JobQueueMapModel
  ): Promise<JobQueueMapListItem> {
    return await this.makePost<JobQueueMapListItem>(
      `jobqueues/map/upsert`,
      request
    );
  }

  public async getJobTypeEnumerationItems(): Promise<EnumerationItemModel[]> {
    const items = await this.makeGet<EnumerationItemModel[]>('enumerations/job-types');

    return items.filter(item =>
      Object.values(JobType).includes(item.value as JobType)
    ) as EnumerationItemModel[]; 
  }
}
